import "./App.css";
import React, { useEffect, useState } from "react";
import { auth } from "./config/firebase";
import { onAuthStateChanged } from "firebase/auth";
import LoginGate from "./components/LoginGate/LoginGate";

// All components: Eager load for consistent skeleton UX
import Header from "./components/Header/Header";
import Home from "./components/Home/Home";
import About from "./components/About/About";
import Skills from "./components/Skills/Skills";
import Certificates from "./components/Certifications/Certificates";
import Qualification from "./components/Qualification/Qualification";
import Work from "./components/Work/Work";
import Contact from "./components/Contact/Contact";
import Themes from "./components/Themes/Themes";
import VisitorsDetails from "./components/VisitorsDetails";
import Footer from "./components/Footer/Footer";
import ScrollUp from "./components/ScrollUp/ScrollUp";
import GlassCursor from "./components/GlassCursor/GlassCursor";
import Chatbot from "./components/Chatbot/Chatbot";

// Admin components
import { AdminProvider } from "./features/admin/AdminProvider";
import AuthButton from "./features/admin/AuthButton";
import AdminPanel from "./features/admin/AdminPanel";

function App() {
  // undefined = auth decision pending (show gate)
  // null      = user skipped sign-in
  // object    = Firebase user who signed in
  const [googleUser, setGoogleUser] = useState(() => {
    // If a Firebase user is already signed in, skip the gate immediately.
    return auth.currentUser
      ? auth.currentUser
      : sessionStorage.getItem("auth_decision")
        ? null
        : undefined;
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setGoogleUser(currentUser);
      } else if (sessionStorage.getItem("auth_decision")) {
        setGoogleUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Prevent browser's automatic scroll restoration
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Handle scroll restoration on page load and hash change
  useEffect(() => {
    const handleScrollRestoration = () => {
      const hash = window.location.hash;
      if (hash) {
        const elementId = hash.substring(1);
        const element = document.getElementById(elementId);
        if (element) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              element.scrollIntoView({ behavior: "auto" });
            });
          });
        }
      } else {
        window.scrollTo(0, 0);
      }
    };

    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => handleScrollRestoration(), { timeout: 2000 });
    } else {
      setTimeout(handleScrollRestoration, 500);
    }

    window.addEventListener("hashchange", handleScrollRestoration);
    return () => {
      window.removeEventListener("hashchange", handleScrollRestoration);
    };
  }, []);

  return (
    <AdminProvider>
      {/* Show login gate until visitor makes an auth decision this session */}
      {googleUser === undefined && <LoginGate onComplete={setGoogleUser} />}
      <Header />
      <VisitorsDetails googleUser={googleUser} />
      <main className="main">
        <Home />
        <About />
        <Skills />
        <Certificates />
        <Qualification />
        <Work />
        <Contact />
        <Themes />
      </main>
      <Footer />
      {/* Portal-based components: only mount after gate is dismissed,
          otherwise they render outside #root and bypass the blur */}
      {googleUser !== undefined && (
        <>
          <ScrollUp />
          <GlassCursor />
          <Chatbot />
        </>
      )}
      {/* Admin Components */}
      <AuthButton />
      <AdminPanel />
    </AdminProvider>
  );
}

export default App;
